const knownNoisyIssues = [
  'WonderPush',
  'Non-Error',
  "Can't find variable: ga",
  'ga is not defined',
  'ga.getAll is not a function',
  'Non-Error promise rejection captured with keys: currentTarget, detail, isTrusted, target',
  'GSI_LOGGER',
  'FedCM',
  'AbortError',
  'Failed to fetch (api.btloader.com)',
  'Load failed (api.btloader.com)',
  'NetworkError when attempting to fetch resource. (api.btloader.com)',
  'Failed to fetch (analytics.google.com)',
  'Failed to fetch (www.google-analytics.com)',
  'Failed to fetch (googletagmanager.com)',
  'Failed to fetch (pagead2.googlesyndication.com)',
  "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection",
  "Failed to read a named property 'performance' from 'Window': An attempt was made to break through the security policy of the user agent",
  "Failed to read a named property 'performance' from 'Window': Blocked a restricted frame with origin",
  "Failed to read a named property 'clearTimeout' from 'Window': An attempt was made to break through the security policy of the user agent",
  "Failed to read a named property 'clearTimeout' from 'Window': Blocked a restricted frame with origin",
  'URIError',
  'URI malformed',
  'Load failed',
  'Got unexpected null or undefined',
  'Java bridge method invocation error',
  // Firefox-only XPCOM internal error thrown by third-party ad/analytics scripts
  // (ads/js/tfav_adl, api/init-*, analytics) when the engine tears a component
  // down mid-scroll. Never raised by first-party code, so it is pure noise.
  'NS_ERROR_NOT_INITIALIZED',
]

const networkFailureMessage = /Failed to fetch|NetworkError when attempting to fetch resource|Network request failed/

const firstPartyAssetUrl = /(?:cgtrader\.com|testcgt\d?\.com)\/(?:packs|assets)\//

const firstPartyHost = /(?:^|\.)(?:cgtrader\.com|testcgt\d?\.com)(?::\d+)?$/i

const absoluteUrlHost = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i

// Host of the request that failed, read off the network breadcrumb closest to the
// rejection. A relative URL carries no host and is ours by definition.
const failedRequestHost = (event) => {
  const crumbs = (event && event.breadcrumbs) || []

  for (let i = crumbs.length - 1; i >= 0; i -= 1) {
    const crumb = crumbs[i]

    if (crumb && (crumb.category === 'fetch' || crumb.category === 'xhr')) {
      const match = absoluteUrlHost.exec((crumb.data && crumb.data.url) || '')

      return match ? match[1] : null
    }
  }

  return null
}

// Injected scripts (extensions, in-app browsers, proxies) resolve their relative
// URLs against our origin, so `allowUrls` waves them through.
export const isInjectedNetworkNoise = (event) => {
  const exception = event && event.exception && event.exception.values && event.exception.values[0]

  if (!exception || !exception.mechanism || exception.mechanism.type !== 'onunhandledrejection') {
    return false
  }

  if (!networkFailureMessage.test(exception.value || '')) {
    return false
  }

  const frames = (exception.stacktrace && exception.stacktrace.frames) || []

  // Chrome builds the TypeError for a network-level fetch failure inside the network stack,
  // so it carries no frames - for our own fetches just as much as for an injected one. A
  // frameless event proves nothing, so it is dropped only when the request that failed
  // points at a third party; without that evidence it is kept.
  if (!frames.length) {
    const host = failedRequestHost(event)

    return Boolean(host) && !firstPartyHost.test(host)
  }

  return !frames.some((frame) => firstPartyAssetUrl.test(frame.filename || ''))
}

// Common Sentry configuration.
// NOTE: tracing/replay sample rates were removed on purpose. They were dead
// config: @sentry/browser v7 does not register BrowserTracing or Replay by
// default, and no `integrations` array adds them, so the rates did nothing.
const sentryConfig = {
  dsn: 'https://5eb8d58117d14638858f28b39d3afe37@o159814.ingest.us.sentry.io/1222718',
  allowUrls: [
    /cgtrader\.com/,
    /assets\.cgtrader\.com/,
    /testcgt\.com/,
    /testcgt2\.com/,
    /testcgt3\.com/
  ],
  autoSessionTracking: true,
  ignoreErrors: knownNoisyIssues,
  beforeSend: (event) => (isInjectedNetworkNoise(event) ? null : event),
}

// @sentry/browser is loaded on demand (see initSentry) so its ~320 kB stay off
// the initial bundle. Until it resolves, capture calls and the current user are
// buffered and replayed once the SDK is ready.
let sentryRef = null
let loadFailed = false
let pendingUser = null
const callQueue = []

function flushPendingUser() {
  if (sentryRef && pendingUser) {
    sentryRef.setUser(pendingUser)
  }
}

// Buffer a capture call until the SDK is ready, or forward it immediately.
// Only the methods used across the app (captureException, captureMessage) are
// proxied — see the synchronous window.Sentry stub below.
function proxyCall(method, args) {
  if (sentryRef) {
    return sentryRef[method](...args)
  }
  // SDK failed to load: drop the call instead of queueing forever.
  if (loadFailed) {
    return undefined
  }
  callQueue.push([method, args])
  return undefined
}

// Synchronous stand-in so the ~30 unguarded `window.Sentry.captureException`
// callers keep working during the 1-3 s before the real SDK loads. Replaced by
// the real module on load (see loadAndInit), so post-load callers get the full
// SDK surface.
const sentryStub = {
  captureException: (...args) => proxyCall('captureException', args),
  captureMessage: (...args) => proxyCall('captureMessage', args),
}

// Initialize Sentry with optional custom config
export const initSentry = (window, customConfig = {}) => {
  const config = { ...sentryConfig, ...customConfig }

  // Synchronous references so error handlers always have something callable,
  // even before the SDK has loaded. Queued calls are replayed once it inits.
  window.Sentry = window.Sentry || sentryStub
  window.sendErrorToSentry = (error, { tags, extra } = {}) => proxyCall('captureException', [error, { tags, extra }])

  const loadAndInit = () => {
    import(/* webpackChunkName: "sentry" */ '@sentry/browser')
      .then((Sentry) => {
        sentryRef = Sentry
        window.Sentry = Sentry
        Sentry.init(config)
        flushPendingUser()
        callQueue.splice(0).forEach(([method, args]) => Sentry[method](...args))
      })
      .catch(() => {
        // SDK failed to load (network/adblock): drop the queue and stop buffering
        // so later capture calls don't grow the queue unbounded.
        loadFailed = true
        callQueue.length = 0
      })
  }

  // Delay loading + initialization until the browser is idle to keep it off the
  // critical path and out of the initial bundle parse.
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(loadAndInit, { timeout: 3000 })
  } else {
    window.setTimeout(loadAndInit, 1000)
  }
}

// Set user information (buffered until the SDK has loaded).
export const setSentryUser = (uuid, username) => {
  if (uuid && uuid.length > 0) {
    pendingUser = { id: uuid, username }
    flushPendingUser()
  }
}
